/**
 * SMART SEARCH ENGINE
 * Modern search system for Pokémon GO database
 * 
 * Features:
 * - Instant client-side search (<100ms)
 * - Intelligent ranking (Exact > Partial > Fuzzy)
 * - Multi-language support (Thai, English)
 * - ID normalization (#025, 025, 25)
 * - Lightweight fuzzy matching
 */

/**
 * Normalize Pokémon ID
 * Handles: "25", "025", "#025", "#25" -> "25"
 */
export function normalizePokemonId(input) {
  if (!input) return '';
  
  // Remove # prefix
  let normalized = String(input).replace(/^#+/, '').trim();
  
  // Extract numeric part
  const numericMatch = normalized.match(/^(\d+)/);
  if (numericMatch) {
    return numericMatch[1];
  }
  
  return normalized;
}

/**
 * Normalize search query
 * - Lowercase
 * - Remove extra spaces
 * - Handle Thai characters
 */
export function normalizeQuery(query) {
  if (!query) return '';
  
  return String(query)
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Calculate search score for ranking
 * Pokédex-grade relevance scoring - accuracy over quantity
 * Higher score = better match
 * 
 * Scoring Rules:
 * - Exact matches = highest priority
 * - Starts-with = high priority (only if query length >= 2)
 * - Contains = medium (only if query length >= 3)
 * - Fuzzy = low (only if very similar, score > 0.85)
 * 
 * IMPORTANT: Only highly relevant results should appear.
 * Wrong results are worse than missing results.
 */
export function calculateSearchScore(pokemon, query, normalizedQuery) {
  let score = 0;
  
  if (!query || !pokemon) return 0;
  
  const id = String(pokemon.id || pokemon.Pokemon_ID || '').toLowerCase();
  const nameEn = String(pokemon.name_en || pokemon.Pokemon_Name_EN || pokemon.name_english || '').toLowerCase();
  const nameTh = String(pokemon.name_th || pokemon.Pokemon_Name_TH || pokemon.name_thai || '').toLowerCase();
  const speciesId = String(pokemon.species_id || pokemon.name_species_id || '').toLowerCase();
  
  const normalizedId = normalizePokemonId(query);
  const queryLower = normalizedQuery || normalizeQuery(query);
  const queryLength = queryLower.length;
  
  // POKÉDEX-GRADE RELEVANCE SCORING
  // Priority: Thai > English > ID > Fuzzy
  // Accuracy over quantity - wrong results are worse than missing results
  
  // TIER 1: Exact Matches (Highest Priority) - Return immediately
  
  // 1. Exact Thai name match (1000 points) - HIGHEST PRIORITY for Pokédex
  if (nameTh && nameTh === queryLower) {
    score += 1000;
    return score; // Exact Thai match - highest relevance
  }
  
  // 2. Exact ID match (950 points) - Very high priority
  if (normalizedId && /^\d+$/.test(normalizedId)) {
    if (id === normalizedId || id === normalizePokemonId(id)) {
      score += 950;
      return score; // Exact ID match
    }
  }
  
  // 3. Exact English name match (900 points)
  if (nameEn && nameEn === queryLower) {
    score += 900;
    return score; // Exact English match
  }
  
  // 4. Exact species ID match (850 points)
  if (speciesId && speciesId === queryLower) {
    score += 850;
    return score;
  }
  
  // TIER 2: Prefix Matches (High Priority) - Only if query ≥ 2 chars
  
  if (queryLength >= 2) {
    // 5. Thai prefix match (800 points) - HIGHER than English for Pokédex
    if (nameTh && nameTh.startsWith(queryLower)) {
      score += 800;
      return score; // Strong Thai prefix match
    }
    
    // 6. English prefix match (700 points)
    if (nameEn && nameEn.startsWith(queryLower)) {
      score += 700;
      return score; // Strong English prefix match
    }
  }
  
  // TIER 3: Contains Matches (Medium Priority) - Only if query ≥ 3 chars
  
  if (queryLength >= 3) {
    // 7. Thai contains (400 points) - HIGHER than English
    if (nameTh && nameTh.includes(queryLower)) {
      // Additional validation: ensure meaningful match
      const matchIndex = nameTh.indexOf(queryLower);
      const beforeMatch = nameTh.substring(0, matchIndex);
      const afterMatch = nameTh.substring(matchIndex + queryLength);
      
      // Prefer matches at word boundaries or start of name
      if (matchIndex === 0 || beforeMatch.endsWith(' ') || beforeMatch.length === 0) {
        score += 400;
        return score; // Good Thai contains match
      } else {
        score += 300; // Still good, but lower priority
        return score;
      }
    }
    
    // 8. English contains (300 points)
    if (nameEn && nameEn.includes(queryLower)) {
      const matchIndex = nameEn.indexOf(queryLower);
      const beforeMatch = nameEn.substring(0, matchIndex);
      
      if (matchIndex === 0 || beforeMatch.endsWith(' ') || beforeMatch.length === 0) {
        score += 300;
        return score; // Good English contains match
      } else {
        score += 200; // Lower priority
        return score;
      }
    }
  }
  
  // TIER 4: ID Contains (Lower Priority) - Only numeric queries
  if (normalizedId && /^\d{2,}$/.test(normalizedId) && id.includes(normalizedId)) {
    score += 250;
    return score;
  }
  
  // TIER 5: Fuzzy Match (Lowest Priority - Very Strict)
  // Only if query ≥ 4 chars AND ≥90% similarity
  // Prevents "โคคูน" from matching unrelated Pokémon
  if (queryLength >= 4 && score === 0) {
    const fuzzyScoreTh = nameTh ? calculateFuzzyScore(nameTh, queryLower) : 0;
    const fuzzyScoreEn = nameEn ? calculateFuzzyScore(nameEn, queryLower) : 0;
    const bestFuzzyScore = Math.max(fuzzyScoreTh, fuzzyScoreEn);
    
    // EXTREMELY strict threshold: 90% similarity (Pokédex-grade)
    if (bestFuzzyScore >= 0.90) {
      score += Math.round(bestFuzzyScore * 150); // Max 135 points for perfect fuzzy
      return score;
    }
  }
  
  return score;
}

/**
 * Strict fuzzy matching using character overlap
 * Pokédex-grade: Only returns high similarity scores
 * Returns similarity score 0-1
 * 
 * Improved algorithm:
 * - Checks character positions and ordering
 * - Penalizes missing characters
 * - Only returns high scores for very similar strings
 */
function calculateFuzzyScore(str1, str2) {
  if (!str1 || !str2) return 0;
  if (str1.length === 0 || str2.length === 0) return 0;
  
  // Normalize: trim and lowercase
  const s1 = str1.trim().toLowerCase();
  const s2 = str2.trim().toLowerCase();
  
  if (s1 === s2) return 1.0;
  
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  
  if (longer.length === 0) return 1.0;
  
  // Improved similarity calculation
  // Count matching characters in order (character sequence matters)
  let orderedMatches = 0;
  let shorterIndex = 0;
  
  for (let i = 0; i < longer.length && shorterIndex < shorter.length; i++) {
    if (longer[i] === shorter[shorterIndex]) {
      orderedMatches++;
      shorterIndex++;
    }
  }
  
  // Calculate similarity based on ordered matches
  const orderedSimilarity = orderedMatches / shorter.length;
  
  // Also check overall character overlap (for unordered similarity)
  const charSet1 = new Set(longer.split(''));
  const charSet2 = new Set(shorter.split(''));
  let overlapCount = 0;
  
  for (const char of charSet2) {
    if (charSet1.has(char)) {
      overlapCount++;
    }
  }
  
  const overlapSimilarity = overlapCount / charSet2.size;
  
  // Use the average, but weight ordered matches more (70% ordered, 30% overlap)
  const finalScore = (orderedSimilarity * 0.7) + (overlapSimilarity * 0.3);
  
  // Pokédex-grade: Only return high similarity scores (≥90%)
  // This ensures "โคคูน" doesn't match unrelated Pokémon
  // Lower threshold returns 0 to prevent false positives
  return finalScore >= 0.90 ? finalScore : 0;
}

/**
 * Highlight matched text in string
 * Returns array of parts: [{text, matched: boolean}]
 */
export function highlightMatch(text, query, normalizedQuery) {
  if (!text || !query) {
    return [{ text, matched: false }];
  }
  
  const normalized = normalizedQuery || normalizeQuery(query);
  const textLower = String(text).toLowerCase();
  const index = textLower.indexOf(normalized);
  
  if (index === -1) {
    // Try fuzzy highlight (highlight similar parts)
    return [{ text, matched: false }];
  }
  
  const before = text.substring(0, index);
  const matched = text.substring(index, index + normalized.length);
  const after = text.substring(index + normalized.length);
  
  const parts = [];
  if (before) parts.push({ text: before, matched: false });
  if (matched) parts.push({ text: matched, matched: true });
  if (after) parts.push({ text: after, matched: false });
  
  return parts;
}

/**
 * Smart Search Engine Class
 * Handles all search logic
 */
export class SmartSearchEngine {
  constructor(pokemonList = []) {
    this.pokemonList = pokemonList;
    this.index = this.buildIndex(pokemonList);
  }
  
  /**
   * Build search index from Pokemon list
   * Pre-processes data for faster search
   */
  buildIndex(pokemonList) {
    return pokemonList.map(pokemon => ({
      ...pokemon,
      _searchable: {
        id: String(pokemon.id || pokemon.Pokemon_ID || '').toLowerCase(),
        idNormalized: normalizePokemonId(pokemon.id || pokemon.Pokemon_ID || ''),
        nameEn: String(pokemon.name_en || pokemon.Pokemon_Name_EN || pokemon.name_english || '').toLowerCase(),
        nameTh: String(pokemon.name_th || pokemon.Pokemon_Name_TH || pokemon.name_thai || '').toLowerCase(),
        speciesId: String(pokemon.species_id || pokemon.name_species_id || '').toLowerCase(),
        keywords: [
          String(pokemon.id || pokemon.Pokemon_ID || ''),
          String(pokemon.name_en || pokemon.Pokemon_Name_EN || pokemon.name_english || ''),
          String(pokemon.name_th || pokemon.Pokemon_Name_TH || pokemon.name_thai || ''),
          String(pokemon.species_id || pokemon.name_species_id || '')
        ].map(k => normalizeQuery(k)).filter(Boolean).join(' ')
      }
    }));
  }
  
  /**
   * Search Pokemon with intelligent ranking
   * Pokédex-grade: Only returns highly relevant results
   * 
   * @param {string} query - Search query
   * @param {object} options - Search options
   * @param {number} options.limit - Maximum results (default: 10)
   * @param {number} options.minScore - Minimum score threshold (default: 200 for relevance)
   * @returns {array} - Array of relevant Pokemon (empty if no matches)
   */
  search(query, options = {}) {
    if (!query || query.trim().length === 0) {
      return [];
    }
    
    const {
      limit = 10, // Reduced default limit (quality over quantity)
      minScore = 200 // Default threshold: requires at least "Contains" match (300) or ID match (200)
    } = options;
    
    const normalizedQuery = normalizeQuery(query);
    const normalizedId = normalizePokemonId(query);
    const queryLength = normalizedQuery.length;
    
    // Dynamic threshold based on query length (Pokédex-grade strictness)
    // Shorter queries need higher scores (more specific matches)
    // Longer queries can have slightly lower scores (more context)
    let dynamicThreshold = minScore;
    if (queryLength === 1) {
      dynamicThreshold = 950; // Single char: only exact ID or exact name matches
    } else if (queryLength === 2) {
      dynamicThreshold = 700; // 2 chars: only prefix matches or exact
    } else if (queryLength === 3) {
      dynamicThreshold = 300; // 3 chars: contains allowed
    } else if (queryLength >= 4) {
      dynamicThreshold = 200; // 4+ chars: contains or fuzzy (if ≥90% similarity)
    }
    
    // Quick early return if ID search (exact match only)
    if (normalizedId && /^\d+$/.test(normalizedId)) {
      const exactIdMatch = this.index.find(p => 
        p._searchable.idNormalized === normalizedId
      );
      
      if (exactIdMatch) {
        return [exactIdMatch];
      }
      // No exact ID match found - return empty (don't show irrelevant results)
      return [];
    }
    
    // Calculate scores for all Pokemon
    const scoredResults = this.index
      .map(pokemon => ({
        pokemon,
        score: calculateSearchScore(pokemon, query, normalizedQuery)
      }))
      .filter(result => result.score > dynamicThreshold) // Apply strict threshold
      .sort((a, b) => {
        // Sort by score (descending)
        if (b.score !== a.score) {
          return b.score - a.score;
        }
        // If same score, sort by ID (ascending)
        const idA = parseInt(a.pokemon.id || a.pokemon.Pokemon_ID || '0') || 0;
        const idB = parseInt(b.pokemon.id || b.pokemon.Pokemon_ID || '0') || 0;
        return idA - idB;
      })
      .slice(0, limit)
      .map(result => result.pokemon);
    
    return scoredResults;
  }
  
  /**
   * Update Pokemon list and rebuild index
   */
  updatePokemonList(pokemonList) {
    this.pokemonList = pokemonList;
    this.index = this.buildIndex(pokemonList);
  }
}
